import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createFinanceApi } from "../api";
import { formatLabel, formatMoney, moneyTone } from "../labels";
import type { PayoutLineItemDto } from "../types";
import {
  AddLineItemDialog,
  ReasonApprovalDialog,
} from "../components/FinanceDialogs";
import { RefundCustomerStatusPanel } from "../components/RefundCustomerStatusPanel";

function DetailShell({
  crumb,
  title,
  description,
  status,
  loading,
  error,
  onRetry,
  onAction,
  children,
}: {
  crumb: string;
  title: string;
  description?: string | undefined;
  status?: string | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onAction?: (() => void) | undefined;
  children: ReactNode;
}) {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Finance", to: "/app/finance" },
    { label: crumb, to: `/app/finance/${crumb.toLowerCase().replaceAll(" ", "-")}` },
    { label: title },
  ]);

  if (loading) return <LoadingState message="Loading…" />;
  if (error) {
    return (
      <EmptyState
        title="Record unavailable"
        description={error}
        reason="scope"
        action={
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        {...(description ? { description } : {})}
        {...(onAction
          ? {
              actions: (
                <Button variant="secondary" onClick={onAction}>
                  Status actions
                </Button>
              ),
            }
          : {})}
      />
      {status ? (
        <StatusChip tone={moneyTone(status)}>{formatLabel(status)}</StatusChip>
      ) : null}
      {children}
    </Stack>
  );
}

function Lines({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <Card>
      <Stack gap={8}>
        {items.map((item) => (
          <Text key={item.label} role="body" as="p">
            {item.label}: {item.value}
          </Text>
        ))}
      </Stack>
    </Card>
  );
}

export function TransactionDetailPage() {
  const { transactionId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<
    ReturnType<typeof api.getTransaction>
  >["transaction"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getTransaction(transactionId);
      setRow(result.transaction);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, transactionId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Transactions"
        title={row?.id ?? "Transaction"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Type", value: formatLabel(row.transaction_type) },
              { label: "Amount", value: formatMoney(row.amount, row.currency) },
              { label: "Fee", value: row.fee_amount },
              { label: "Commission", value: row.commission_amount },
              { label: "Booking", value: row.booking_id ?? "—" },
              { label: "Payout", value: row.payout_id ?? "—" },
              { label: "External reference", value: row.external_reference ?? "—" },
              { label: "Occurred", value: row.occurred_at },
            ]}
          />
        ) : null}
        {row?.booking_id ? (
          <Button
            variant="secondary"
            onClick={() => void navigate(`/app/bookings/${row.booking_id}`)}
          >
            Open Booking
          </Button>
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Transaction action"
        notice="mark_refunded_* only updates this money record. Booking refund status is a separate Bookings action."
        actions={[
          "submit",
          "succeed",
          "fail",
          "reverse",
          "mark_refunded_partial",
          "mark_refunded_full",
          "dispute",
          "settle",
          "reconcile",
        ]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyTransactionAction(transactionId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Transaction updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function RefundDetailPage() {
  const { refundId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<ReturnType<typeof api.getRefund>>["refund"] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getRefund(refundId)).refund);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, refundId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A provider refund (i36) moves only by Xendit's answer; the one human
  // action is resending it after it failed.
  const providerRefund = Boolean(row?.provider);
  const canAct = row ? (providerRefund ? Boolean(row.retryable) : true) : false;

  return (
    <>
      <DetailShell
        crumb="Refunds"
        title={row?.id ?? "Refund"}
        {...(providerRefund
          ? {
              description: row?.retryable
                ? "Executed through Xendit. The provider refused it; you can resend it."
                : "Executed through Xendit. It updates when Xendit reports the outcome.",
            }
          : {})}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={canAct ? () => setActionOpen(true) : undefined}
      >
        {row ? (
          <Lines
            items={[
              { label: "Amount", value: row.amount },
              { label: "Reason type", value: row.reason_type },
              { label: "Transaction", value: row.transaction_id },
              ...(providerRefund
                ? [
                    { label: "Provider", value: row.provider ?? "—" },
                    { label: "Provider refund", value: row.provider_refund_id ?? "Not reported yet" },
                    { label: "Attempt", value: String(row.attempt_count ?? 0) },
                    { label: "Last attempt", value: row.last_attempted_at ?? "—" },
                    { label: "Failure", value: row.failure_code ?? "—" },
                  ]
                : [
                    { label: "Requester", value: row.requested_by_user_id ?? "—" },
                    { label: "Approver", value: row.approved_by_user_id ?? "—" },
                    { label: "Issuer", value: row.issued_by_user_id ?? "—" },
                  ]),
            ]}
          />
        ) : null}
        {row && providerRefund ? (
          <RefundCustomerStatusPanel refund={row} onChanged={() => void load()} />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        key={providerRefund ? "provider" : "bookkeeping"}
        open={actionOpen}
        title={providerRefund ? "Resend refund to Xendit" : "Refund action"}
        notice={
          providerRefund
            ? "Only a refund Xendit reported as failed can be resent. It goes out with a new attempt key; the outcome arrives from Xendit."
            : "Approve and execute must be different people from the requester. Dual Approval ID is required at or above 1000."
        }
        actions={providerRefund ? ["provider_retry"] : [
          "start_review",
          "approve",
          "reject",
          "execute",
          "settle",
          "mark_failed",
          "require_intervention",
        ]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            if (providerRefund) {
              await api.retryProviderRefund(refundId, {
                ...(input.reason ? { reason: input.reason } : {}),
              });
              toast.push({ title: "Refund resent to Xendit", tone: "success" });
            } else {
              await api.applyRefundAction(refundId, input.action as never, {
                ...(input.reason ? { reason: input.reason } : {}),
                ...(input.approval_id ? { approval_id: input.approval_id } : {}),
              });
              toast.push({ title: "Refund updated", tone: "success" });
            }
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function PayoutDetailPage() {
  const { payoutId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<ReturnType<typeof api.getPayout>>["payout"] | null>(
    null,
  );
  const [lines, setLines] = useState<PayoutLineItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [lineOpen, setLineOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, items] = await Promise.all([
        api.getPayout(payoutId),
        api.listPayoutLineItems(payoutId),
      ]);
      setRow(detail.payout);
      setLines(items.line_items);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, payoutId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Payouts"
        title={row?.id ?? "Payout"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Amount", value: formatMoney(row.amount, row.currency) },
              { label: "Business", value: row.business_id ?? "—" },
              { label: "Creator", value: row.creator_profile_id ?? "—" },
              { label: "Hold reason", value: row.hold_reason ?? "—" },
            ]}
          />
        ) : null}
        <Flex justify="space-between" align="center">
          <Text role="h2" as="h2">
            Line items
          </Text>
          <Button size="sm" variant="secondary" onClick={() => setLineOpen(true)}>
            Add line item
          </Button>
        </Flex>
        {lines.length === 0 ? (
          <EmptyState title="No line items" reason="none" />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Transaction</Th>
                <Th>Gross</Th>
                <Th>Fee</Th>
                <Th>Commission</Th>
                <Th>Net</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lines.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.transaction_id}</Td>
                  <Td>{item.gross_amount}</Td>
                  <Td>{item.fee_amount}</Td>
                  <Td>{item.commission_amount}</Td>
                  <Td>{item.net_amount}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Payout action"
        notice="Hold and release of material payouts need Dual Approval. HQ does not send the bank transfer."
        holdReason
        actions={[
          "submit_review",
          "approve",
          "start_processing",
          "mark_paid",
          "settle",
          "reconcile",
          "hold",
          "delay",
          "fail",
          "dispute",
          "release_hold",
        ]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyPayoutAction(payoutId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
              ...(input.hold_reason ? { hold_reason: input.hold_reason } : {}),
            });
            toast.push({ title: "Payout updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <AddLineItemDialog
        open={lineOpen}
        payoutId={payoutId}
        busy={busy}
        error={actionError}
        onPreview={async (transactionId) =>
          (await api.previewPayoutLineItem(payoutId, transactionId)).preview
        }
        onClose={() => {
          setLineOpen(false);
          setActionError(null);
        }}
        onSubmit={async (body) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.addPayoutLineItem(payoutId, body);
            toast.push({ title: "Line item added", tone: "success" });
            setLineOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function SettlementDetailPage() {
  const { settlementId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<
    ReturnType<typeof api.getSettlement>
  >["settlement"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getSettlement(settlementId)).settlement);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, settlementId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Settlements"
        title={row?.id ?? "Settlement"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Payout", value: row.payout_id },
              { label: "Expected", value: row.expected_amount },
              { label: "Settled", value: row.settled_amount ?? "—" },
              { label: "Reference", value: row.settlement_reference ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Settlement action"
        actions={["settle", "reconcile", "mark_unmatched", "fail", "resolve_unmatched"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applySettlementAction(settlementId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Settlement updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function CommissionRuleDetailPage() {
  const { ruleId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<
    ReturnType<typeof api.getCommissionRule>
  >["commission_rule"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getCommissionRule(ruleId)).commission_rule);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, ruleId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Commission Rules"
        title={row?.id ?? "Commission Rule"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Scope type", value: row.rule_scope_type },
              { label: "Scope ID", value: row.rule_scope_id },
              { label: "Effective from", value: row.effective_from },
              { label: "Effective to", value: row.effective_to ?? "—" },
              { label: "Approved by", value: row.approved_by_user_id ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Commission rule action"
        notice="Activate requires Super Admin approval and replaces the prior active rule."
        actions={["approve", "schedule", "activate", "replace", "retire"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyCommissionAction(ruleId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approval_id ? { approval_id: input.approval_id } : {}),
            });
            toast.push({ title: "Commission rule updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function ExceptionDetailPage() {
  const { exceptionId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<
    ReturnType<typeof api.getException>
  >["exception"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getException(exceptionId)).exception);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, exceptionId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Exceptions"
        title={row?.id ?? "Exception"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Type", value: formatLabel(row.exception_type) },
              { label: "Severity", value: formatLabel(row.severity) },
              { label: "Transaction", value: row.transaction_id ?? "—" },
              { label: "Payout", value: row.payout_id ?? "—" },
              { label: "Owner", value: row.owner_user_id ?? "—" },
              { label: "Resolution", value: row.resolution_reason ?? "—" },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Exception action"
        actions={["investigate", "await_info", "resolve", "escalate", "close"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyExceptionAction(exceptionId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.action === "resolve" && input.reason
                ? { resolution_reason: input.reason }
                : {}),
            });
            toast.push({ title: "Exception updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}

export function DisputeDetailPage() {
  const { disputeId = "" } = useParams();
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [row, setRow] = useState<Awaited<
    ReturnType<typeof api.getDispute>
  >["dispute"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRow((await api.getDispute(disputeId)).dispute);
    } catch (err) {
      setError(apiErrorMessage(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [api, disputeId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <DetailShell
        crumb="Disputes"
        title={row?.id ?? "Dispute"}
        status={row?.status}
        loading={loading}
        error={error}
        onRetry={() => void load()}
        onAction={() => setActionOpen(true)}
      >
        {row ? (
          <Lines
            items={[
              { label: "Reason", value: row.reason },
              { label: "Transaction", value: row.transaction_id ?? "—" },
              { label: "Refund", value: row.refund_id ?? "—" },
              { label: "Owner", value: row.owner_user_id ?? "—" },
              { label: "Opened", value: row.opened_at },
            ]}
          />
        ) : null}
      </DetailShell>
      <ReasonApprovalDialog
        open={actionOpen}
        title="Dispute action"
        actions={["investigate", "await_info", "resolve", "escalate", "close"]}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyDisputeAction(disputeId, input.action as never, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({ title: "Dispute updated", tone: "success" });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
